import React, { useState, useEffect, useCallback } from "react";
import Table from "../../../components/ui/table";
import styles from "./invoices.module.css";
import { MyIcons } from "../../../components/icons/my-icons";
import { useModal } from "../../../contexts/ModalContext";
import { formatDateToSpanish } from "../../../utils/dateUtils";
import { getVerifactuActionsForDocument } from "../../../utils/verifactu";
import { 
  getInvoiceSummary, 
  deletePrefactura,
  getPrefacturaById,
  getLocalities,
  generateFacturaPDF,
  generatePrefacturaPDF,
  generateFacturaRectificativaPDF,
  getBudgetForInvoice
} from "../../../services/apiService";
import { useParams, useOutletContext } from "react-router-dom";
import InvoiceViewModal from "../../../components/modals/InvoiceViewModal";
import usePageTitle from "../../../hooks/usePageTitle";

const getDocumentTypeDisplay = (tipo) => {
  switch (tipo) {
    case "Factura":
      return "Factura";
    case "Pre-factura":
    case "Prefactura":
      return "Pre-factura";
    case "Rectificativa":
      return "F. Rectificativa";
    default:
      return tipo;
  }
};

const formatInvoiceData = (apiData, expedienteId) => {
  return apiData.map(presupuesto => ({
    type: "presupuesto",
    number: presupuesto.numero,
    subjectInfo: presupuesto.asunto,
    date: formatDateToSpanish(presupuesto.fecha),
    facturado: `${presupuesto.facturado}€ de ${presupuesto.presupuestoTotal}€`,
    paymentStatus: "",
    feeStatus: "",
    presupuestoId: presupuesto.presupuestoId,
    presupuestoTotal: presupuesto.presupuestoTotal,
    presupuestoData: { ...presupuesto, expedienteId }, // Incluir expedienteId
    invoices: presupuesto.documentos.map(doc => ({
      type: getDocumentTypeDisplay(doc.tipo),
      number: doc.numero,
      subjectInfo: `${parseFloat(doc.porcentaje).toFixed(2)}%`,
      date: formatDateToSpanish(doc.fecha),
      facturado: `${doc.facturado}€`,
      verifactu: doc.verifactu || "-",
      estadoVerifactu: doc.verifactu || "",
      paymentStatus: doc.cobro === null ? "-" : (doc.cobro ? "cobrado" : "pendiente"),
      feeStatus: doc.honorarios === null ? "-" : (doc.honorarios ? "pagado" : "pendiente"),
      tipoCobro: doc.tipoCobro || "-",
      pendienteCobro: (doc.cobro === false && doc.facturado != null)
        ? `${parseFloat(doc.facturado).toFixed(2)}€`
        : "-",
      isInvoice: true,
      documentId: doc.id,
      documentType: getDocumentTypeDisplay(doc.tipo)
    }))
  }));
};

const Invoices = () => {
  const { id: expedienteId } = useParams();
  usePageTitle(`Expediente ${expedienteId}`, 'Facturas');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { openModal } = useModal();
  const { isLockedByOther, refetchStatistics } = useOutletContext();
  
  // Estados para el modal de visualización
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [selectedInvoiceType, setSelectedInvoiceType] = useState(null);
  const [subsanacionMode, setSubsanacionMode] = useState(false);

  const fetchInvoiceSummary = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getInvoiceSummary(expedienteId);
      console.log("Raw invoice response:", response);
      const formattedData = formatInvoiceData(response, expedienteId);
      console.log("Formatted invoice data:", formattedData);
      setData(formattedData);
    } catch (error) {
      console.error("Error fetching invoice summary:", error);
    } finally {
      setLoading(false);
    }
  }, [expedienteId]);

  useEffect(() => {
    if (expedienteId) {
      fetchInvoiceSummary();
    }
  }, [expedienteId, fetchInvoiceSummary]);

  const headers = [
    { key: "type", label: "Tipo" },
    { key: "number", label: "Número" },
    { key: "subjectInfo", label: "Asunto / Porcentaje" },
    { key: "date", label: "Fecha" },
    { key: "facturado", label: "Facturado" },
    { key: "verifactu", label: "Verifactu" },
    { key: "tipoCobro", label: "Tipo de cobro" },
    { key: "pendienteCobro", label: "Pendiente" },
    { key: "paymentStatus", label: "Estado de cobro" },
    { key: "feeStatus", label: "Honorarios" },
  ];

  const textColorConditions = {
    paymentStatus: [
      {
        test: (value) => value === "cobrado",
        color: "var(--green)",
      },
      {
        test: (value) => value === "pendiente",
        color: "var(--red)",
      },
    ],
    feeStatus: [
      {
        test: (value) => value === "pagado",
        color: "var(--green)",
      },
      {
        test: (value) => value === "pendiente",
        color: "var(--red)",
      },
    ],
    pendienteCobro: [
      {
        test: (value) => value !== "-",
        color: "var(--red)",
      },
    ],
  };



  const handleDeletePrefactura = async (documentId) => {
    if (!window.confirm("¿Estás seguro de que quieres eliminar esta pre-factura?")) {
      return;
    }
    
    try {
      await deletePrefactura(documentId);
      fetchInvoiceSummary(); // Refresh data
      if (refetchStatistics) refetchStatistics(); // Actualizar estadísticas
    } catch (error) {
      console.error("Error deleting prefactura:", error);
    }
  };

  const handleCreateRectificativa = (documentId) => {
    // Abrir modal para pedir el motivo
    openModal("RectificativaModal", { 
      documentId, 
      onSuccess: () => {
        fetchInvoiceSummary();
        if (refetchStatistics) refetchStatistics();
      }
    });
  };

  const handleCreateFacturaFromPrefactura = async (documentId, editMode = false) => {
    console.log("=== handleCreateFacturaFromPrefactura called ===");
    console.log("documentId:", documentId);
    console.log("editMode:", editMode);
    
    try {
      // Fetch pre-factura details
      console.log("Fetching prefactura details...");
      const response = await getPrefacturaById(documentId);
      console.log("Prefactura response:", response);
      // La API devuelve los datos directamente, no en response.data
      const prefactura = response.data || response;
      console.log("Prefactura data:", prefactura);
      
      // Get localidades for dropdown lookup
      console.log("Fetching localities...");
      const localidadesResponse = await getLocalities();
      console.log("Localities response:", localidadesResponse);
      const localidades = localidadesResponse.data || localidadesResponse;
      console.log("Localities array:", localidades);
      const localidad = localidades.find(loc => loc.id === prefactura.localidadFacturacionId);
      console.log("Found localidad:", localidad);
      
      // Cargar el presupuesto para obtener el IVA (igual que InvoiceViewModal)
      let budgetData = null;
      if (prefactura.presupuestoId) {
        try {
          budgetData = await getBudgetForInvoice(prefactura.presupuestoId);
          console.log("Budget data loaded:", budgetData);
        } catch (budgetError) {
          console.error("Error loading budget for IVA:", budgetError);
        }
      }
      
      // Transform data to match modal form structure
      const ivaFromBudget = budgetData?.iva;
      console.log("IVA from budget:", ivaFromBudget);
      const prefillData = {
        numero: prefactura.numFactura || "",
        fecha: prefactura.fecha || "",
        tipoCobro: prefactura.tipoCobro ? { 
          label: prefactura.tipoCobro, 
          value: prefactura.tipoCobro 
        } : null,
        iva: ivaFromBudget ? { label: ivaFromBudget, value: ivaFromBudget } : null,
        costePresupuesto: prefactura.costeTotal?.toString() || "",
        costeFactura: prefactura.costeFinal?.toString() || "",
        porcentaje: prefactura.porcentage?.toString() || "",
        clientType: prefactura.destinatarioFactura ? {
          label: prefactura.destinatarioFactura,
          value: prefactura.destinatarioFactura
        } : null,
        nombre: prefactura.nombreFacturacion || "",
        domicilio: prefactura.domicilioFacturacion || "",
        localidad: localidad ? {
          label: `${localidad.municipio} (${localidad.codigoPostal})`,
          value: localidad.id,
          municipio: localidad.municipio,
          codigoPostal: localidad.codigoPostal
        } : null,
        nif: prefactura.nifFacturacion || "",
        presupuestoId: prefactura.presupuestoId,
        expedienteId: prefactura.expedienteId
      };
      
      // Si está en modo edición, abrir InvoiceCreateModal con editingPrefacturaId
      if (editMode) {
        openModal("InvoiceCreateModal", {
          ...prefillData,
          editingPrefacturaId: documentId,
          onSuccess: () => {
            fetchInvoiceSummary();
            if (refetchStatistics) refetchStatistics();
          }
        });
      } else {
        openModal("PrefacturaToFacturaModal", {
          prefacturaData: prefillData,
          prefacturaId: documentId,
          onSuccess: () => {
            fetchInvoiceSummary();
            if (refetchStatistics) refetchStatistics();
          }
        });
      }
      
      console.log("Modal opened successfully");
    } catch (error) {
      console.error("Error fetching prefactura details:", error);
      console.error("Error stack:", error.stack);
    }
  };

  const handleSendInvoice = (documentId, documentType) => {
    openModal("SendInvoiceModal", {
      invoiceId: documentId,
      expedienteId: expedienteId,
      documentType: documentType,
      onSuccess: () => {
        fetchInvoiceSummary();
      }
    });
  };

  const handleSubsanarFactura = (documentId) => {
    setSelectedInvoiceId(documentId);
    setSelectedInvoiceType("Factura");
    setSubsanacionMode(true);
    setViewModalOpen(true);
  };

  const handleDownloadFactura = async (documentId, documentType) => {
    try {
      console.log("Descargar PDF:", documentType, documentId);
      let fileUrl;
      
      // Usar el endpoint correcto según el tipo de documento
      if (documentType === "Pre-factura") {
        fileUrl = await generatePrefacturaPDF(documentId);
      } else if (documentType === "F. Rectificativa") {
        fileUrl = await generateFacturaRectificativaPDF(documentId);
      } else {
        fileUrl = await generateFacturaPDF(documentId);
      }
      
      console.log("URL del archivo:", fileUrl);
      
      // Llamar al WordLauncher en silencio (sin abrir pestaña)
      await fetch(fileUrl).catch(() => {});
    } catch (error) {
      console.error("Error al descargar PDF:", error);
      // Aquí podrías agregar un toast de error si lo deseas
    }
  };

  const renderActions = (row) => {
    // Distinguimos si es una factura dentro de un presupuesto
    if (row.isInvoice) {
      const { documentType, documentId } = row;
      const verifactuState = String(row.estadoVerifactu || row.verifactu || "")
        .trim()
        .toUpperCase();
      const verifactuActions = getVerifactuActionsForDocument(documentType, verifactuState);
      
      return (
        <div className={styles.actionButtons}>
          {verifactuActions.showRegularActions && <>
          <button
            className={styles.actionButton}
            disabled={isLockedByOther}
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadFactura(documentId, documentType);
            }}
            title="Descargar PDF"
          >
            <MyIcons name="downloadPdf" width={24} height={24} color={"var(--main)"} />
          </button>
          {/* Enviar factura */}
          <button
            className={styles.actionButton}
            disabled={isLockedByOther}
            onClick={(e) => {
              e.stopPropagation();
              handleSendInvoice(documentId, documentType);
            }}
            title="Enviar"
          >
            <MyIcons name="send" width={24} height={24} color={"var(--main)"} />
          </button>
          </>}
          {verifactuActions.showSubsanar && (
            <button
              className={styles.subsanarButton}
              disabled={isLockedByOther}
              onClick={(e) => {
                e.stopPropagation();
                handleSubsanarFactura(documentId);
              }}
              title="Corregir datos de facturación y enviar subsanación VeriFactu"
            >
              Subsanar
            </button>
          )}
          {verifactuActions.showAdminWarning && (
            <span
              className={styles.verifactuWarning}
              role="img"
              aria-label="AVISAR AL ADMINISTRADOR DEL SISTEMA"
              title="AVISAR AL ADMINISTRADOR DEL SISTEMA"
            >
              !
            </span>
          )}
          {/* Acciones específicas según el tipo */}
          {documentType === "Pre-factura" && (
            <>
              <button
                className={styles.actionButton}
                disabled={isLockedByOther}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeletePrefactura(documentId);
                }}
                title="Borrar pre-factura"
              >
                <MyIcons name="deleteInvoice" width={24} height={24} color={"var(--red)"} />
              </button>
            </>
          )}

          {documentType === "Factura" && verifactuActions.showRegularActions && (
            <>
              <button
                className={styles.actionButton}
                disabled={isLockedByOther}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreateRectificativa(documentId);
                }}
                title="Rectificar"
              >
                <MyIcons name="rectify" width={24} height={24} color={"var(--main)"} />
              </button>
            </>
          )}

          {/* Rectificativa no tiene acciones específicas */}
        </div>
      );
    } else {
      // Es un presupuesto
      return (
        <div className={styles.actionButtons}>
          <button
            className={styles.actionButton}
            disabled={isLockedByOther}
            onClick={(e) => {
              e.stopPropagation();
              openModal("InvoiceCreateModal", { 
                ...row.presupuestoData,
                onSuccess: () => {
                  fetchInvoiceSummary();
                  if (refetchStatistics) refetchStatistics();
                }
              });
            }}
            title="Crear factura"
          >
            <MyIcons name="createInvoice" width={24} height={24} color={"var(--main)"} />
          </button>
        </div>
      );
    }
  };

  const handleRowClick = (row) => {
    console.log("Fila seleccionada:", row);
    
    // Solo abrir modal si es una factura (no un presupuesto padre)
    if (row.isInvoice && row.documentId) {
      // Si es una pre-factura, abrir modal en modo edición
      if (row.documentType === "Pre-factura") {
        handleCreateFacturaFromPrefactura(row.documentId, true);
      } else {
        // Para facturas y rectificativas, abrir modal de visualización
        setSelectedInvoiceId(row.documentId);
        setSelectedInvoiceType(row.documentType);
        setViewModalOpen(true);
      }
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <p>Cargando facturas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Table
        headers={headers}
        data={data}
        onPress={isLockedByOther ? null : handleRowClick}
        renderActions={renderActions}
        textColorConditions={textColorConditions}
        expandAllByDefault={true}
        getInvoiceRowClass={(invoice, tableStyles) => (
          getVerifactuActionsForDocument(
            invoice.documentType,
            invoice.estadoVerifactu || invoice.verifactu
          ).showAdminWarning
            ? tableStyles.verifactuAdminAlertRow
            : ""
        )}
        getInvoiceRowTitle={(invoice) => (
          getVerifactuActionsForDocument(
            invoice.documentType,
            invoice.estadoVerifactu || invoice.verifactu
          ).showAdminWarning
            ? "AVISAR AL ADMINISTRADOR DEL SISTEMA"
            : ""
        )}
      />
      
      {/* Modal de visualización de factura */}
      <InvoiceViewModal
        isOpen={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedInvoiceId(null);
          setSelectedInvoiceType(null);
          setSubsanacionMode(false);
        }}
        invoiceId={selectedInvoiceId}
        invoiceType={selectedInvoiceType}
        subsanacionMode={subsanacionMode}
        onSuccess={() => {
          fetchInvoiceSummary();
          if (refetchStatistics) refetchStatistics();
        }}
      />
    </div>
  );
};

export default Invoices;
